import React from 'react';
import { Users, Calendar, Clock, Star } from 'lucide-react';
import { BanquetHall } from '../types';
import { format, isSameDay } from 'date-fns';
import { cn } from '../utils/cn';
import { useDashboardStore } from '../stores/dashboardStore';

interface WeeklyBanquetGridProps {
  halls: BanquetHall[];
  calendarDates: Date[];
  selectedHallId?: string;
  onBookingSelected: (bookingId: number) => void;
  onEmptySpaceClicked: (hallId: string, date: Date) => void;
}

const WeeklyBanquetGrid: React.FC<WeeklyBanquetGridProps> = ({
  halls,
  calendarDates,
  selectedHallId,
  onBookingSelected,
  onEmptySpaceClicked,
}) => {
  const { allBanquetBookings } = useDashboardStore();
  
  // Sort halls by name
  const sortedHalls = [...halls].sort((a, b) => a.hallName.localeCompare(b.hallName));

  const isToday = (date: Date) => {
    const now = new Date();
    return isSameDay(date, now);
  };

  const getEventTypeColor = (eventType: string) => {
    switch (eventType?.toLowerCase()) {
      case 'wedding':
        return 'bg-pink-100 border-pink-200 text-pink-800';
      case 'corporate':
        return 'bg-blue-100 border-blue-200 text-blue-800';
      case 'birthday':
        return 'bg-yellow-100 border-yellow-200 text-yellow-800';
      case 'conference':
        return 'bg-purple-100 border-purple-200 text-purple-800';
      default:
        return 'bg-gray-100 border-gray-200 text-gray-800';
    }
  };

  const getEventTypeIcon = (eventType: string) => {
    switch (eventType?.toLowerCase()) {
      case 'wedding':
        return '💒';
      case 'corporate':
        return '🏢';
      case 'birthday':
        return '🎂';
      case 'conference':
        return '📊';
      default:
        return '🎉';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="flex">
          {/* Hall Column Header */}
          <div className="w-80 p-4 bg-gray-100 border-r border-gray-200">
            <h3 className="font-semibold text-gray-800">Banquet Halls</h3>
          </div>
          
          {/* Date Headers */}
          <div className="flex-1 flex">
            {calendarDates.map((date, index) => (
              <div key={index} className="flex-1 p-2 text-center border-r border-gray-200 last:border-r-0 relative">
                <div className={cn(
                  "text-xs font-medium mb-1",
                  isToday(date) ? "text-blue-600" : "text-gray-600"
                )}>
                  {format(date, 'EEE')}
                </div>
                <div className={cn(
                  "text-sm font-bold",
                  isToday(date) ? "text-blue-700" : "text-gray-800"
                )}>
                  {format(date, 'MMM dd')}
                </div>
                {/* Current day indicator line */}
                {isToday(date) && (
                  <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-red-500"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Grid Content */}
      <div>
        {sortedHalls.map((hall) => {
          const isSelected = selectedHallId === hall.hallId;
          
          // Calculate dynamic row height based on maximum bookings for this hall
          const hallBookings = allBanquetBookings.filter(booking => booking.hallId === hall.hallId);
          const bookingsByDate = hallBookings.reduce((acc, booking) => {
            const eventDate = new Date(booking.eventDate);
            const dateIndex = calendarDates.findIndex(date => isSameDay(date, eventDate));
            if (dateIndex !== -1) {
              if (!acc[dateIndex]) acc[dateIndex] = [];
              acc[dateIndex].push(booking);
            }
            return acc;
          }, {} as Record<number, any[]>);
          
          const maxBookingsPerDate = Math.max(...Object.values(bookingsByDate).map(bookings => bookings.length), 0);
          const minHeight = 160; // Minimum height for 0-1 bookings (h-40 = 160px)
          const bookingHeight = 80; // Height per booking (80px)
          // Increase height when there are 2 or more bookings to always show empty space below
          // 2 bookings = 240px (3 slots), 3 bookings = 320px (4 slots), etc.
          const dynamicHeight = maxBookingsPerDate >= 2 ? (maxBookingsPerDate + 1) * bookingHeight : minHeight;
          
          return (
            <div
              key={hall.hallId}
              className={cn(
                "flex border-b border-gray-200 hover:bg-gray-50 transition-colors",
                isSelected && "bg-blue-50"
              )}
            >
              {/* Hall Info */}
              <div className={cn(
                "w-80 p-4 border-r border-gray-200 flex items-center",
                isSelected && "bg-blue-50"
              )}>
                <div className="flex-1">
                  <div className="font-semibold text-gray-800 mb-1">{hall.hallName}</div>
                  <div className="text-sm text-gray-600 mb-2">{hall.hallType}</div>
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <div className="flex items-center space-x-1">
                      <Users className="w-3 h-3" />
                      <span>{hall.capacity} guests</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Occupancy Row */}
              <div 
                className="flex-1 relative"
                style={{ height: `${dynamicHeight}px` }}
              >
                {/* Current day indicator line */}
                {calendarDates.map((date, index) => {
                  if (isToday(date)) {
                    return (
                      <div
                        key={`indicator-${index}`}
                        className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                        style={{ left: `calc(${(index / calendarDates.length) * 100}% + 1px)` }}
                      />
                    );
                  }
                  return null;
                })}

                {/* Empty space clickable areas */}
                {calendarDates.map((date, index) => {
                  const dayWidth = 100 / calendarDates.length;
                  const leftPercent = (index / calendarDates.length) * 100;
                  
                  // Check if there's a booking for this hall on this date
                  const bookingsForThisDate = allBanquetBookings.filter(booking => {
                    if (booking.hallId !== hall.hallId) return false;
                    const eventDate = new Date(booking.eventDate);
                    return isSameDay(eventDate, date) && 
                           (booking.bookingStatus === 'confirmed' || booking.bookingStatus === 'completed');
                  });
                  
                  const hasBooking = bookingsForThisDate.length > 0;

                  return (
                    <div
                      key={`empty-${index}`}
                      className="absolute top-0 bottom-0 cursor-pointer hover:bg-gray-100 transition-colors"
                      style={{
                        left: `${leftPercent}%`,
                        width: `${dayWidth}%`
                      }}
                      onClick={(e) => {
                        // Calculate which booking slot was clicked
                        const rect = e.currentTarget.getBoundingClientRect();
                        const clickY = e.clientY - rect.top;
                        const bookingHeight = 80; // Height per booking in pixels
                        const clickedSlot = Math.floor(clickY / bookingHeight);
                        
                        // Allow clicking if:
                        // 1. No bookings exist, OR
                        // 2. Clicking on an empty slot (beyond existing bookings)
                        if (!hasBooking || clickedSlot >= bookingsForThisDate.length) {
                          onEmptySpaceClicked(hall.hallId, date);
                        }
                      }}
                    />
                  );
                })}
                
                {/* Booking bubbles */}
                {(() => {
                  // Get all bookings for this hall
                  const hallBookings = allBanquetBookings.filter(booking => booking.hallId === hall.hallId);
                  
                  const bookingRanges: any[] = [];
                  
                  // Process each booking for this hall
                  hallBookings.forEach(booking => {
                    const eventDate = new Date(booking.eventDate);
                    
                    // Find which calendar date this booking is on
                    const dateIndex = calendarDates.findIndex(date => isSameDay(date, eventDate));
                    
                    if (dateIndex !== -1) {
                      const bookingRange = {
                        dateIndex: dateIndex,
                        status: booking.bookingStatus === 'confirmed' ? 'Confirmed' : 
                               booking.bookingStatus === 'completed' ? 'Completed' :
                               booking.bookingStatus === 'cancelled' ? 'Cancelled' : 'Pending',
                        guestName: booking.guestName,
                        eventName: booking.eventName,
                        eventType: booking.eventType,
                        startTime: booking.startTime,
                        endTime: booking.endTime,
                        guestCount: booking.guestCount,
                        booking: booking,
                      };
                      
                      bookingRanges.push(bookingRange);
                    }
                  });
                  
                  // Group bookings by date to handle multiple bookings per date
                  const bookingsByDate = bookingRanges.reduce((acc, booking) => {
                    if (!acc[booking.dateIndex]) {
                      acc[booking.dateIndex] = [];
                    }
                    acc[booking.dateIndex].push(booking);
                    return acc;
                  }, {} as Record<number, any[]>);
                  
                  return Object.entries(bookingsByDate).map(([dateIndex, bookings]) => {
                    const dayWidth = 100 / calendarDates.length;
                    const leftPercent = (parseInt(dateIndex) / calendarDates.length) * 100;
                    
                    return (bookings as any[]).map((booking: any, bookingIndex: number) => {
                      // Calculate dynamic positioning for any number of bookings
                      const bookingHeight = 80; // Height per booking in pixels
                      const topPosition = bookingIndex * bookingHeight;
                      const heightPercent = (bookingHeight / dynamicHeight) * 100;
                      
                      return (
                        <div
                          key={`${dateIndex}-${bookingIndex}`}
                          className={cn(
                            "absolute rounded-lg px-2 py-1 flex flex-col justify-center cursor-pointer hover:opacity-80 transition-opacity border m-0.5",
                            getEventTypeColor(booking.eventType)
                          )}
                          style={{
                            left: `${leftPercent}%`,
                            width: `${dayWidth}%`,
                            height: `${heightPercent}%`,
                            top: `${topPosition}px`
                          }}
                          onClick={() => onBookingSelected(booking.booking.bookingId)}
                        >
                          <div className="flex items-center space-x-1 mb-1">
                            <span className="text-xs">{getEventTypeIcon(booking.eventType)}</span>
                            <Clock className="w-2.5 h-2.5" />
                          </div>
                          <div className="text-xs font-semibold truncate leading-tight mb-0.5">
                            {booking.eventName || 'Event'}
                          </div>
                          <div className="text-xs leading-tight mb-0.5">
                            {booking.guestName || 'Guest'}
                          </div>
                          <div className="text-xs leading-tight">
                            {booking.startTime} - {booking.endTime}
                          </div>
                          <div className="text-xs leading-tight">
                            {booking.guestCount} guests
                          </div>
                        </div>
                      );
                    });
                  }).flat();
                })()}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WeeklyBanquetGrid;
